import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { FILTER_HEALTH_TIER_LEVELS } from 'dpl/constants/query_params';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import SmartLink from 'dpl/components/SmartLink';
import { BREED_HEALTH_TESTING_GUIDE_PATH } from 'dpl/shared/constants/urls';
import { healthTiersResourceDefinition } from 'dpl/breeder_search_results/utils/queryDefinitions';
import Banner from 'dpl/common/design_system/Banner';
import { HealthTiersResponse } from 'dpl/breeder_search_results/constants/resource_types';

import MultiSelectChecklistFilter from './MultiSelectChecklistFilter';

interface BreederBreedHealthFilterProps {
  title?: string | null;
  subtitle?: React.ReactNode | null;
}

function getFilterDescriptionFromHealthTier(title: string) {
  switch (title) {
    case 'Good':
      return 'Basic breed-recommended health tests.';
    case 'Great':
      return 'Expanded testing beyond the basics, such as additional breed-specific tests.';
    case 'Excellent':
      return 'The most comprehensive breed-specific testing, including checks for less common conditions.';
    default:
      return null;
  }
}
export default function BreederBreedHealthFilter({
  title,
  subtitle
}: BreederBreedHealthFilterProps) {
  const { breedSlug, breedNamePlural } = useSelector(
    ({ server }: any) => server
  );

  const {
    value: { data: healthTiers }
  } = useResourceQuery<HealthTiersResponse>(healthTiersResourceDefinition());

  const healthTierOptions = useMemo(
    () =>
      healthTiers?.map((tier: any) => {
        return {
          title: `${tier.title} level testing`,
          value: tier.id,
          long_description: getFilterDescriptionFromHealthTier(tier.title)
        };
      }),
    [healthTiers]
  );

  return (
    <>
      <MultiSelectChecklistFilter
        filterTitle={title}
        // @ts-ignore
        filterSubtitle={subtitle}
        filterParam={FILTER_HEALTH_TIER_LEVELS}
        filterOptions={healthTierOptions}
      />
      <Banner
        color="teal"
        iconName="fetch-info-24"
        iconVariant="light"
        type="horizontal"
        className="items-center mt6"
      >
        <div className="font-14 secondary">
          Health testing levels reflect the parents&apos; results. Breeders
          provide documentation to earn a level.{' '}
          <SmartLink
            className="stone-700 underline"
            to={`/breeds/${breedSlug}${BREED_HEALTH_TESTING_GUIDE_PATH}`}
            target="_blank"
          >
            Learn more
          </SmartLink>{' '}
          about health testing for {breedNamePlural}.
        </div>
      </Banner>
    </>
  );
}

BreederBreedHealthFilter.defaultProps = {
  title: null,
  subtitle: null
};
